import type { AccountCapacity } from './types';

/**
 * Conta de slot, sem transporte. O saldo por passbook, a cobrança PIX e o
 * livro de lançamentos ficam em `features/channel-quota`; aqui só a conta que
 * a revisão de campanha e a barra da sidebar precisam repetir igual.
 */

export type QuotaSignal = 'ok' | 'approaching' | 'exhausted';

export const QUOTA_APPROACHING_RATIO = 0.8;

export interface SlotSendPlan {
  people: number;
  unitsPerPerson: number;
  requestedSlots: number;
  /** Pessoas que cabem na cota restante, no limite da campanha e no da empresa. */
  sendPeople: number;
  slotsToReserve: number;
  leftOut: number;
  canSendPartial: boolean;
  quotaEmpty: boolean;
}

type QuotaLevel = Pick<AccountCapacity, 'available' | 'sent' | 'cycleLimit'>;

export function quotaLimit(quota: QuotaLevel): number {
  if (quota.cycleLimit > 0) return quota.cycleLimit;
  return Math.max(0, quota.sent) + Math.max(0, quota.available);
}

/** Percentual de slots que ainda restam. A barra desce conforme os envios gastam a cota. */
export function remainingPercent(quota: QuotaLevel): number {
  const limit = quotaLimit(quota);
  if (limit <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((Math.max(0, quota.available) / limit) * 100)));
}

export function quotaSignal(quota: QuotaLevel): QuotaSignal {
  if (quota.available <= 0) return 'exhausted';
  const limit = quotaLimit(quota);
  if (limit > 0 && quota.sent / limit >= QUOTA_APPROACHING_RATIO) return 'approaching';
  return 'ok';
}

/**
 * Envio parcial usa a cota restante inteira: os primeiros contatos que cabem
 * em `available`, sem passar do limite da campanha nem do da empresa.
 */
export function planSlotSend(people: number, unitsPerPerson: number, capacity: AccountCapacity): SlotSendPlan {
  const units = Math.max(0, Math.floor(unitsPerPerson));
  const audience = Math.max(0, Math.floor(people));
  const requestedSlots = audience > 0 && units > 0 ? audience * units : 0;
  const cap = Math.max(0, Math.min(capacity.available, capacity.companyLimit, capacity.campaignLimit));
  const sendPeople = units > 0 ? Math.min(audience, Math.floor(cap / units)) : 0;
  const slotsToReserve = sendPeople * units;
  const leftOut = Math.max(0, audience - sendPeople);
  return {
    people: audience,
    unitsPerPerson: units,
    requestedSlots,
    sendPeople,
    slotsToReserve,
    leftOut,
    canSendPartial: audience > 0 && units > 0 && sendPeople > 0 && sendPeople < audience,
    quotaEmpty: audience > 0 && units > 0 && capacity.available <= 0,
  };
}
